import { Autocomplete, Box, Chip, CircularProgress, Grid } from "@mui/material"
import Button from "@mui/material/Button"
import Dialog from "@mui/material/Dialog"
import DialogActions from "@mui/material/DialogActions"
import DialogContent from "@mui/material/DialogContent"
import DialogTitle from "@mui/material/DialogTitle"
import TextField from "@mui/material/TextField"
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers"
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment"
import { useFormik } from "formik"
import { useAppDispatch, useAppSelector } from "hooks"
import { useEffect, useState } from "react"
import licensePoolService from "services/licensePoolService"
import licenseService from "services/licenseService"
import trainingModuleService from "services/trainingModuleService"
import { addLicensePool } from "slices/licensePoolSlice"
import { setTrainingModules } from "slices/trainingModuleSlice"
import type { ICreateLicensePool } from "types/licensePoolTypes"
import type { ICreateLicense } from "types/licenseTypes"
import type { IOrganization } from "types/organizationTypes"
import * as Yup from "yup"

type Props = {
  organization: IOrganization
}

export function CreateLicensePoolDialog({ organization }: Props) {
  const [isLoading, setIsLoading] = useState<boolean>(false)
  const [open, setOpen] = useState(false)
  const [numberOfLicenses, setNumberOfLicenses] = useState(0)

  const dispatch = useAppDispatch()

  const trainingModules = useAppSelector((state) => state.trainingmodule.trainingModules)

  useEffect(() => {
    if (open === false) {
      return
    }

    trainingModuleService
      .getTrainingModules()
      .then((trainingModules) => {
        const sortedModules = trainingModules.sort((a, b) =>
          a.simulatorApplicationId > b.simulatorApplicationId
            ? 1
            : b.simulatorApplicationId > a.simulatorApplicationId
              ? -1
              : 0,
        )

        dispatch(setTrainingModules(sortedModules))
      })
      .catch((err) => {
        console.log(err)
      })
  }, [open, dispatch])

  const initialValues: ICreateLicensePool = {
    name: "",
    contractEndDate: new Date(),
    licenseDuration: 9,
    licenseOfflineDuration: 0,
    organizationId: "",
    trainingModuleIds: [] as string[],
  }

  const createLicensePoolValidationScheme = Yup.object({
    name: Yup.string()
      .required("Please enter application name")
      .min(4, "Minimum length is 4 characters")
      .max(64, "Maximum length is 64 characters"),
    contractEndDate: Yup.date().required("Please enter contract end date"),
    licenseDuration: Yup.number().required("Please enter minimum license duration in months"),
    licenseOfflineDuration: Yup.number().required(
      "Please enter minimum duration license can be accessed offline (Days)",
    ),
  })

  const formik = useFormik({
    initialValues: initialValues,
    validationSchema: createLicensePoolValidationScheme,
    onSubmit: (values) => {
      setIsLoading(true)

      const createLisencePool: ICreateLicensePool = {
        name: values.name,
        contractEndDate: values.contractEndDate,
        licenseDuration: values.licenseDuration,
        licenseOfflineDuration: values.licenseOfflineDuration,
        organizationId: organization.id,
        trainingModuleIds: values.trainingModuleIds,
      }

      licensePoolService
        .createLicensePool(createLisencePool)
        .then((licensePool) => {
          dispatch(addLicensePool(licensePool))

          if (numberOfLicenses < 1) {
            setIsLoading(false)
            handleClose()
            return
          }
          const createLicense: ICreateLicense = {
            licensePoolId: licensePool.id,
            numberOfLicenses: numberOfLicenses,
          }
          licenseService
            .createLicense(createLicense)
            .then((_licenses) => {
              setIsLoading(false)
              handleClose()
            })
            .catch((err) => {
              console.log(err)
              setIsLoading(false)
            })
        })
        .catch((err) => {
          console.log(err)
          setIsLoading(false)
        })
    },
  })

  const handleClickOpen = () => {
    formik.resetForm()
    setOpen(true)
  }

  const handleClose = () => {
    setOpen(false)
  }

  return (
    <div>
      <Button data-testid="createLicensePoolButton" variant="contained" onClick={handleClickOpen}>
        CREATE LICENSE POOL
      </Button>
      <Dialog open={open} fullWidth maxWidth="sm">
        <form onSubmit={formik.handleSubmit}>
          <DialogTitle>Create License Pool</DialogTitle>
          <DialogContent>
            <Grid container spacing={1}>
              <Grid item xs={12}>
                <TextField
                  required
                  autoFocus
                  margin="dense"
                  id="name"
                  label="Name"
                  type="name"
                  fullWidth
                  variant="outlined"
                  value={formik.values.name}
                  onChange={formik.handleChange}
                  error={formik.touched.name && Boolean(formik.errors.name)}
                  helperText={formik.touched.name && formik.errors.name}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  margin="dense"
                  id="licenseDuration"
                  label="Minimum License Duration (Months)"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={formik.values.licenseDuration}
                  onChange={formik.handleChange}
                  error={formik.touched.licenseDuration && Boolean(formik.errors.licenseDuration)}
                  helperText={formik.touched.licenseDuration && formik.errors.licenseDuration}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  margin="dense"
                  id="licenseOfflineDuration"
                  label="License Offline Access Duration (Days)"
                  type="number"
                  fullWidth
                  variant="outlined"
                  value={formik.values.licenseOfflineDuration}
                  onChange={formik.handleChange}
                  error={
                    formik.touched.licenseOfflineDuration &&
                    Boolean(formik.errors.licenseOfflineDuration)
                  }
                  helperText={
                    formik.touched.licenseOfflineDuration && formik.errors.licenseOfflineDuration
                  }
                />
              </Grid>
              <Grid item xs={12}>
                <LocalizationProvider dateAdapter={AdapterMoment}>
                  <DesktopDatePicker
                    label="Contract End Date"
                    inputFormat="DD/MM/YYYY"
                    value={formik.values.contractEndDate}
                    onChange={(date) => formik.setFieldValue("contractEndDate", date)}
                    renderInput={(params) => <TextField {...params} />}
                  />
                </LocalizationProvider>
              </Grid>
              <Grid item xs={12}>
                <Autocomplete
                  multiple
                  id="trainingModules"
                  filterSelectedOptions
                  disableCloseOnSelect
                  options={trainingModules}
                  getOptionLabel={(trainingmodule) => trainingmodule.name}
                  groupBy={(module) => module.simulatorApplicationName}
                  renderTags={(selected, getTagProps) =>
                    selected.map((module, index) => (
                      <Chip
                        {...getTagProps({ index })}
                        key={module.id}
                        label={`${module.simulatorApplicationName}: ${module.name}`}
                      />
                    ))
                  }
                  onChange={(_event, selectedModules) =>
                    formik.setFieldValue(
                      "trainingModuleIds",
                      selectedModules.map((x) => x.id),
                    )
                  }
                  renderInput={(params) => (
                    <TextField
                      {...params}
                      variant="outlined"
                      label="Select training modules"
                      placeholder="Training Module"
                    />
                  )}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  margin="dense"
                  id="numberOfLicenses"
                  label="Number of licenses"
                  type="number"
                  fullWidth
                  variant="outlined"
                  onChange={(e) => setNumberOfLicenses(Number(e.target.value))}
                  defaultValue={0}
                />
              </Grid>
            </Grid>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose}>Cancel</Button>
            <Box sx={{ m: 1, position: "relative" }}>
              <Button type="submit" disabled={isLoading}>
                Create
              </Button>
              {isLoading && (
                <CircularProgress
                  size={24}
                  sx={{
                    position: "absolute",
                    top: "50%",
                    left: "50%",
                    marginTop: "-12px",
                    marginLeft: "-12px",
                  }}
                />
              )}
            </Box>
          </DialogActions>
        </form>
      </Dialog>
    </div>
  )
}
